import { CabinOverridesPayload, SeatLayoutEntry, SeatRowSelection } from '../charter.types';

type OrderedFacilityRow = { layout: Array<SeatLayoutEntry>; overrideOrder: number };

type ResolvedRowLayouts = {
  primaryLayout: Array<SeatLayoutEntry>;
  facilityRowsBefore: Array<OrderedFacilityRow>;
  facilityRowsAfter: Array<OrderedFacilityRow>;
};

type FloatingFacilityRow = {
  rowNumber: number;
  layout: Array<SeatLayoutEntry>;
  overrideOrder: number;
};

/**
 * Expands a SeatRowSelection into a flat array of row numbers.
 * SeatRowSelection is an array that can contain:
 *   - Individual row numbers (number)
 *   - Row ranges ({ start: number, end: number })
 */
export const expandRowSelection = (selection: SeatRowSelection): Array<number> => {
  const rows: Array<number> = [];

  selection.forEach((item) => {
    if (typeof item === 'number') {
      rows.push(item);
    } else {
      for (let row = item.start; row <= item.end; row += 1) {
        rows.push(row);
      }
    }
  });

  return rows;
};

export const resolveRowLayouts = (
  baseLayout: Array<SeatLayoutEntry>,
  cabinOverrides: Array<CabinOverridesPayload['overrides'][number]>,
  rowNumber: number,
): ResolvedRowLayouts => {
  const facilityRowsBefore: Array<OrderedFacilityRow> = [];
  const facilityRowsAfter: Array<OrderedFacilityRow> = [];
  let layout = baseLayout;

  cabinOverrides.forEach((override, overrideIndex) => {
    if (!override.seatLayout) {
      return;
    }

    const rows = expandRowSelection(override.rows);

    if (!rows.includes(rowNumber)) {
      return;
    }

    const hasSeatEntries = override.seatLayout.some((entry) => entry.kind === 'seat');

    if (hasSeatEntries) {
      layout = override.seatLayout;
      return;
    }

    const targetCollection = override.rowPosition === 'after' ? facilityRowsAfter : facilityRowsBefore;

    targetCollection.push({ layout: override.seatLayout, overrideOrder: overrideIndex });
  });

  return {
    primaryLayout: layout,
    facilityRowsBefore,
    facilityRowsAfter,
  };
};

export const collectFloatingFacilityRows = (
  cabinOverrides: Array<CabinOverridesPayload['overrides'][number]>,
  cabinRowSelection: SeatRowSelection,
): Array<FloatingFacilityRow> => {
  const floatingRows: Array<FloatingFacilityRow> = [];
  const cabinRows = new Set(expandRowSelection(cabinRowSelection));

  cabinOverrides.forEach((override, overrideIndex) => {
    if (!override.seatLayout) {
      return;
    }

    const hasSeatEntries = override.seatLayout.some((entry) => entry.kind === 'seat');

    if (hasSeatEntries) {
      return;
    }

    const rows = expandRowSelection(override.rows);

    rows.forEach((rowNumber) => {
      const withinBaseRange = cabinRows.has(rowNumber);

      if (withinBaseRange) {
        return;
      }

      floatingRows.push({
        rowNumber,
        layout: override.seatLayout,
        overrideOrder: overrideIndex,
      });
    });
  });

  return floatingRows;
};

export const resolveSeatOverrides = (
  cabinOverrides: Array<CabinOverridesPayload['overrides'][number]>,
  rowNumber: number,
  seatLetter: string,
): Array<CabinOverridesPayload['overrides'][number]> =>
  cabinOverrides.filter((override) => {
    const rows = expandRowSelection(override.rows);
    const rowMatches = rows.includes(rowNumber);
    const seatMatches = !override.seatLetters || override.seatLetters.includes(seatLetter);

    return rowMatches && seatMatches;
  });
